import { Box, Typography, IconButton, Paper, styled, Dialog, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useContext, useState } from "react";
import { useNavigate } from "react-router";
import { v4 } from "uuid";
import UserContext from "../../../../../Context/UserProvider";
import { NotificationContext } from "../../../../common/Notifications/NotificationProvider";
import { useApiService } from "../../../../../services/ApiService/ApiService";
import toast from 'react-hot-toast';

interface InputFormProp {}

const DropZone = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'hasFiles'
})<{ isActive?: boolean; hasFiles?: boolean }>(({ theme, isActive, hasFiles }) => ({
  border: `2px dashed ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  backgroundColor: isActive ? theme.palette.action.hover : theme.palette.background.paper,
  transition: theme.transitions.create(['border', 'background-color']),
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  ...(hasFiles && {
    border: 'none'
  })
}));

const UploadInput = styled('input')({
  display: 'none'
});

function UploadTabScreen(props: InputFormProp) {
  const {userInfo} = useContext(UserContext);
  const notificationContext = useContext(NotificationContext);
  const { fetchWithAuth } = useApiService();
  const navigate = useNavigate();
  
  const [fileContent, setFileContent] = useState([
    { fileName: "", fileSize: 0, file: File, id: 0 },
  ]);
  const [hoverZone, setHoverZone] = useState(false);
  const [fileUploadExceed, setFileUploadExceed] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleCloseModal = () => setIsOpen(false);

  const onDragEnter = (e: React.DragEvent): void => {
    e.preventDefault();
    setHoverZone(true);
  };

  const onDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    setHoverZone(false);
  };

  const toBase64 = (file: File) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  function addNewFile(currentFiles: any[], newFile: any) {
    let result = [] as any;
    for (let i = 0; i < newFile?.length; i++) {
      if (
        currentFiles
          ?.map((e: any) => e.fileName)
          .indexOf(newFile[i].fileName) < 0
      ) {
        result.push(newFile[i]);
      }
    }
    return result;
  }

  const converFileBase64 = async (file: any) => {
    let files_list_arr = [];
    for (let i = 0; i < file.length; i++) {
      let current_file: any = await toBase64(file[i].file);
      files_list_arr.push({
        originalFilename: file[i].fileName,
        encodedFile: current_file.slice(21),
      });
    }
    return files_list_arr;
  };

  const submitForm = async (fileContent: any) => {
    setIsUploading(true);
    let fileToUpload = addNewFile([] as any, fileContent);
    removeFile();
    setFileUploaded(true);
    
    if (fileToUpload.length <= 0) {
      toast.error('File already been upload');
      removeFile();
      setIsUploading(false);
      return;
    }

    let bodyData: { [name: string]: string } = {};
    bodyData["userId"] = userInfo.uuid;
    bodyData["fleetUuid"] = userInfo.fleetUuid;

    let files_list_arr = await converFileBase64(fileToUpload);
    bodyData["fileList"] = JSON.stringify(files_list_arr);
    
    try {
      const response = await fetchWithAuth('/DragDrop/saveFiles', {
        method: "POST",
        body: JSON.stringify(bodyData),
      });
      
      const res_obj = await response.json();
      
      // Handle specific HTTP status codes
      if (response.status === 401) {
        toast.error('Authentication failed. Please log in again.');
        setFileUploaded(false);
        return;
      }
      
      if (response.status === 413) {
        toast.error('File(s) too large for upload. Please compress or split your files.');
        setFileUploaded(false);
        return;
      }
      
      if (response.status !== 200) {
        // Try to get error message from response
        let errorMessage = 'Upload failed';
        if (res_obj && res_obj.notifications && res_obj.notifications.length > 0) {
          if (res_obj.notifications[0].code === "FREE_ACCOUNT_UPLOADS_EXCEEDED") {
            errorMessage = `You have exceeded the maximum number of 10 file uploads available for FREE Tier membership`;
            setFileUploadExceed(true);
          } else {
            errorMessage = res_obj.notifications[0].code || res_obj.notifications[0].message || 'Upload failed';
          }
        } else if (res_obj && res_obj.message) {
          errorMessage = res_obj.message;
        } else if (res_obj && res_obj.error) {
          errorMessage = res_obj.error;
        }

        toast.error(errorMessage);
        setFileUploaded(false);
        return;
      }

      toast.success('File finished uploading');
      setIsOpen(true);
      setFileUploaded(true);
    } catch (error) {
      console.error('Upload error:', error);
      
      // Handle different types of errors
      let errorMessage = 'There is an error during upload. Please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (error.message.includes('413') || error.message.includes('Payload')) {
          errorMessage = 'File(s) too large for upload. Please compress or split your files.';
        } else if (error.message.includes('Network') || error.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
      }
      
      toast.error(errorMessage);
      setFileUploaded(false);
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (): void => {
    setFileContent([{ fileName: "", fileSize: 0, file: File, id: 0 }]);
    setFileUploaded(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHoverZone(false);
    let i = 0;
    let files_arr: any[] = [];
    
    if (e.target.files) {
      Array.from(e.target.files)
        .filter(file => file.type === "text/csv" || file.type === "application/zip")
        .forEach(file => {
          files_arr.push({
            fileName: file.name,
            fileSize: file.size,
            file: file,
            id: i++,
          });
        });
      setFileContent(files_arr);
      submitForm(files_arr);
      setFileUploaded(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setHoverZone(false);
    
    let i = 0;
    let files_list: any[] = [];
    
    Array.from(e.dataTransfer.files)
      .filter(file => file.type === "text/csv" || file.type === "application/zip")
      .forEach(file => {
        files_list.push({
          fileName: file.name,
          fileSize: file.size,
          file: file,
          id: i++,
        });
      });
    
    setFileContent(files_list);
    submitForm(files_list);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
      <CloudUploadIcon sx={{ fontSize: 90, mb: 1 }} />
      <Typography variant="h4" gutterBottom>Upload files</Typography>
      <Typography variant="body1" gutterBottom>Files must be in csv format.</Typography>

      <Box sx={{ width: '100%', maxWidth: 600, mt: 3, position: 'relative' }}>
        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Uploading files...
              </Typography>
            </Box>
          </Box>
        )}
        <DropZone
          isActive={hoverZone}
          hasFiles={fileContent[0].fileName.length > 0}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            Drag & Drop your file(s) here or
          </Typography>
          <label>
            <UploadInput
              type="file"
              onChange={handleFileChange}
              accept=".csv,.zip"
              multiple
              disabled={isUploading}
            />
            <Button
              component="span"
              color="primary"
              sx={{ textDecoration: 'underline' }}
              disabled={isUploading}
            >
              browse
            </Button>
          </label>
        </DropZone>
      </Box>

      <Dialog
        open={modalIsOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2
          }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          p: { xs: 2, sm: 4 },
          textAlign: 'center'
        }}>
          <IconButton 
            onClick={handleCloseModal}
            sx={{ 
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ mb: 3 }}>
            <CloudUploadIcon 
              sx={{ 
                fontSize: 64,
                color: 'primary.main',
                mb: 2
              }} 
            />
            <Typography variant="h5" gutterBottom>
              Files Uploaded Successfully
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your files are now being processed
            </Typography>
          </Box>

          <Box sx={{ width: '100%', mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              To track the progress of your uploads:
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() =>
                navigate("/plan/scenario/configuration/ice-vehicles", {
                  state: { showCategory: [false, true] },
                })
              }
              sx={{ mb: 2 }}
            >
              View Upload Progress
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCloseModal}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

export default UploadTabScreen;
